import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Sparkles, UploadCloud, FileText, User, Briefcase, Languages, Save, CheckCircle2, MapPin } from 'lucide-react';
import { toast } from '../../lib/toast';
import { API, auth, inputCls, Field, errMsg } from '../organization/finance/shared';

const COACH_TYPES = { head_coach: 'مدرب رئيسي', assistant_coach: 'مدرب مساعد', goalkeeper_coach: 'مدرب حراس مرمى', fitness_coach: 'مدرب لياقة', conditioning_coach: 'مدرب إعداد بدني', performance_analyst: 'محلل أداء', youth_coach: 'مدرب فئات سنية', other: 'تخصص آخر' };
const EMPLOYMENT_TYPES = { full_time: 'كامل', part_time: 'جزئي', seasonal: 'موسمي', freelance: 'متعاون' };
const AVAILABILITY = { available: 'متاح', available_offers_only: 'متاح للعروض فقط', unavailable: 'غير متاح' };

const tagsToStr = (arr) => (arr || []).join('، ');
const strToTags = (s) => s.split(/[،,]/).map((x) => x.trim()).filter(Boolean);

const Section = ({ icon: Icon, title, hint, children, testId }) => (
  <section className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:p-6" data-testid={testId}>
    <div className="mb-5 flex items-start gap-3 border-b border-slate-100 pb-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
        <Icon size={17} strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <h3 className="text-base font-bold text-slate-800">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      </div>
    </div>
    {children}
  </section>
);

export default function CoachProfileBasicsTab({ profile, refreshProfile }) {
  const [form, setForm] = useState(profile || {});
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => { setForm(profile || {}); }, [profile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const completeness = useMemo(() => {
    const checks = [
      form.full_name_ar, form.phone, form.city, form.nationality, form.bio,
      form.years_experience, form.photo_file_id, (form.languages || []).length,
      (form.skills || []).length, (form.games || []).length,
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [form]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/files/upload`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
      set('photo_file_id', res.data.file_id);
      toast.success('تم رفع الصورة');
    } catch (err) { toast.error(errMsg(err, 'فشل رفع الصورة')); }
    setUploadingPhoto(false);
  };

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCv(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/coaches/documents/upload`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
      set('cv_document_id', res.data.document_id);
      toast.success('تم رفع السيرة الذاتية، يمكنك الآن التعبئة التلقائية');
    } catch (err) { toast.error(errMsg(err, 'فشل رفع الملف')); }
    setUploadingCv(false);
  };

  const extractFromCv = async () => {
    if (!form.cv_document_id) return;
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/coaches/documents/${form.cv_document_id}/extract`, {}, auth());
      const d = res.data || {};
      setForm((f) => ({
        ...f,
        bio: d.bio || f.bio,
        years_experience: d.years_experience ?? f.years_experience,
        languages: (d.languages && d.languages.length) ? d.languages : f.languages,
        education: (d.education && d.education.length) ? d.education : f.education,
        skills: (d.skills && d.skills.length) ? d.skills : f.skills,
      }));
      toast.success('تم استخراج البيانات، راجعها ثم احفظ');
      const certCount = d.certifications_added?.length || 0;
      const expCount = d.experiences_added?.length || 0;
      if (certCount || expCount) {
        toast.success(`أُضيفت ${certCount} شهادة و${expCount} خبرة تلقائياً من ملفك — راجعها وأكمل أي حقول ناقصة في تبويبي "الشهادات" و"الخبرات"`);
      }
    } catch (err) { toast.error(errMsg(err, 'تعذّر استخراج البيانات من الملف')); }
    setExtracting(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        full_name_ar: form.full_name_ar, full_name_en: form.full_name_en, photo_file_id: form.photo_file_id,
        phone: form.phone, email: form.email, nationality: form.nationality, region: form.region, city: form.city,
        bio: form.bio, years_experience: form.years_experience ? Number(form.years_experience) : null,
        games: form.games, coach_type: form.coach_type, custom_specialty: form.custom_specialty,
        age_categories: form.age_categories, languages: form.languages, education: form.education, skills: form.skills,
        intro_video_url: form.intro_video_url, cv_document_id: form.cv_document_id,
        willing_to_relocate: Boolean(form.willing_to_relocate), employment_type: form.employment_type,
        availability_status: form.availability_status,
        expected_salary_min: form.expected_salary_min ? Number(form.expected_salary_min) : null,
        expected_salary_max: form.expected_salary_max ? Number(form.expected_salary_max) : null,
      };
      await axios.put(`${API}/coaches/profile/me`, payload, auth());
      toast.success('تم حفظ الملف المهني');
      refreshProfile?.();
    } catch (err) { toast.error(errMsg(err, 'فشل الحفظ')); }
    setSaving(false);
  };

  const photoUrl = form.photo_file_id ? `${API}/files/${form.photo_file_id}` : null;

  return (
    <form onSubmit={submit} className="space-y-5 pb-24" data-testid="coach-basics-form">
      {/* التعبئة التلقائية من السيرة الذاتية */}
      <section className="relative overflow-hidden rounded-2xl border border-teal-200/70 bg-gradient-to-br from-teal-50 via-white to-emerald-50/60 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:p-6" data-testid="coach-cv-autofill-card">
        <div className="pointer-events-none absolute -left-10 -top-10 h-32 w-32 rounded-full bg-teal-400/10 blur-2xl" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md shadow-teal-500/25">
              <Sparkles size={19} strokeWidth={2.2} />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-slate-800">ابدأ من سيرتك الذاتية</h3>
              <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
                ارفع ملف PDF وسنستخرج النبذة والخبرات والشهادات واللغات والمهارات تلقائياً — ثم راجعها واحفظ.
              </p>
              {form.cv_document_id && (
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-teal-700 ring-1 ring-teal-200" data-testid="coach-cv-uploaded-badge">
                  <FileText size={12} /> ملف السيرة مرفوع
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all hover:border-teal-300 active:scale-95" data-testid="coach-cv-upload-label">
              <UploadCloud size={16} />
              {uploadingCv ? 'جارِ الرفع...' : form.cv_document_id ? 'تحديث الملف' : 'رفع السيرة (PDF)'}
              <input type="file" accept="application/pdf" className="hidden" onChange={uploadCv} data-testid="coach-cv-upload-input" />
            </label>
            <button
              type="button"
              onClick={extractFromCv}
              disabled={extracting || !form.cv_document_id}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-teal-500/25 transition-all hover:brightness-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-45"
              data-testid="coach-cv-autofill-btn"
            >
              <Sparkles size={16} />
              {extracting ? 'جارِ التحليل...' : 'تعبئة تلقائية'}
            </button>
          </div>
        </div>
      </section>

      {/* اكتمال الملف */}
      <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="coach-profile-completeness">
        <div className="mb-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
            <CheckCircle2 size={16} className={completeness === 100 ? 'text-teal-600' : 'text-slate-400'} />
            اكتمال الملف المهني
          </div>
          <span className="text-sm font-extrabold tabular-nums text-teal-700" data-testid="coach-completeness-value">{completeness}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-gradient-to-l from-teal-500 to-emerald-500 transition-all duration-500" style={{ width: `${completeness}%` }} />
        </div>
        <p className="mt-2 text-xs text-slate-500">الملف المكتمل يزيد فرص ظهورك للمنشآت الباحثة عن مدربين.</p>
      </div>

      <Section icon={User} title="الهوية والتواصل" hint="بياناتك الأساسية — الجوال والبريد يظلان مخفيين عن العامة" testId="coach-section-identity">
        <div className="mb-5 flex flex-wrap items-center gap-4">
          <div className="relative">
            {photoUrl
              ? <img src={photoUrl} alt="" className="h-20 w-20 rounded-2xl object-cover ring-2 ring-slate-100" data-testid="coach-photo-preview" />
              : <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><User size={28} /></div>}
          </div>
          <div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition-all hover:border-teal-300 active:scale-95" data-testid="coach-photo-upload-label">
              <UploadCloud size={15} />
              {uploadingPhoto ? 'جارِ الرفع...' : photoUrl ? 'تغيير الصورة' : 'رفع صورة شخصية'}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="coach-photo-upload-input" />
            </label>
            <p className="mt-1.5 text-[11px] text-slate-400">صورة واضحة بخلفية بسيطة، مقاس مربع يفضّل.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="الاسم الكامل بالعربية" required><input className={inputCls} value={form.full_name_ar || ''} onChange={(e) => set('full_name_ar', e.target.value)} data-testid="coach-input-name-ar" /></Field>
          <Field label="الاسم بالإنجليزية (اختياري)"><input className={inputCls} value={form.full_name_en || ''} onChange={(e) => set('full_name_en', e.target.value)} /></Field>
          <Field label="رقم الجوال (يظل مخفياً عن العامة)"><input className={inputCls} value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} data-testid="coach-input-phone" /></Field>
          <Field label="البريد الإلكتروني (يظل مخفياً)"><input className={inputCls} value={form.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="الجنسية"><input className={inputCls} value={form.nationality || ''} onChange={(e) => set('nationality', e.target.value)} /></Field>
          <Field label="المنطقة"><input className={inputCls} value={form.region || ''} onChange={(e) => set('region', e.target.value)} /></Field>
          <Field label="المدينة"><input className={inputCls} value={form.city || ''} onChange={(e) => set('city', e.target.value)} data-testid="coach-input-city" /></Field>
        </div>

        <div className="mt-4">
          <Field label="نبذة مهنية">
            <textarea rows={4} className={inputCls} value={form.bio || ''} onChange={(e) => set('bio', e.target.value)} placeholder="اكتب نبذة مختصرة عن مسيرتك التدريبية وفلسفتك" data-testid="coach-input-bio" />
          </Field>
          <p className="mt-1 text-[11px] text-slate-400">{(form.bio || '').length} حرف</p>
        </div>
      </Section>

      <Section icon={Briefcase} title="التخصص والتوفر" hint="تساعد المنشآت على معرفة ملاءمتك للوظيفة" testId="coach-section-specialty">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="التخصص"><select className={inputCls} value={form.coach_type || 'head_coach'} onChange={(e) => set('coach_type', e.target.value)} data-testid="coach-select-type">{Object.entries(COACH_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          {form.coach_type === 'other' && <Field label="حدد التخصص"><input className={inputCls} value={form.custom_specialty || ''} onChange={(e) => set('custom_specialty', e.target.value)} /></Field>}
          <Field label="سنوات الخبرة"><input type="number" min="0" className={inputCls} value={form.years_experience ?? ''} onChange={(e) => set('years_experience', e.target.value)} data-testid="coach-input-years" /></Field>
          <Field label="نوع التفرغ"><select className={inputCls} value={form.employment_type || 'full_time'} onChange={(e) => set('employment_type', e.target.value)}>{Object.entries(EMPLOYMENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="حالة التوفر"><select className={inputCls} value={form.availability_status || 'available'} onChange={(e) => set('availability_status', e.target.value)} data-testid="coach-select-availability">{Object.entries(AVAILABILITY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="نطاق الراتب المتوقع (اختياري)">
            <div className="flex gap-2">
              <input type="number" placeholder="من" className={inputCls} value={form.expected_salary_min ?? ''} onChange={(e) => set('expected_salary_min', e.target.value)} />
              <input type="number" placeholder="إلى" className={inputCls} value={form.expected_salary_max ?? ''} onChange={(e) => set('expected_salary_max', e.target.value)} />
            </div>
          </Field>
        </div>

        <label className="mt-4 flex w-fit cursor-pointer items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-teal-300" data-testid="coach-relocate-toggle">
          <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={Boolean(form.willing_to_relocate)} onChange={(e) => set('willing_to_relocate', e.target.checked)} />
          <MapPin size={15} className="text-slate-400" /> قابل للانتقال إلى مدينة أخرى
        </label>
      </Section>

      <Section icon={Languages} title="المهارات والمؤهلات" hint="افصل بين العناصر بفاصلة" testId="coach-section-skills">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="الألعاب التي تدربها"><input className={inputCls} value={tagsToStr(form.games)} onChange={(e) => set('games', strToTags(e.target.value))} placeholder="كرة قدم، كرة سلة" /></Field>
          <Field label="الفئات العمرية"><input className={inputCls} value={tagsToStr(form.age_categories)} onChange={(e) => set('age_categories', strToTags(e.target.value))} placeholder="تحت 15، شباب" /></Field>
          <Field label="اللغات"><input className={inputCls} value={tagsToStr(form.languages)} onChange={(e) => set('languages', strToTags(e.target.value))} placeholder="العربية، الإنجليزية" /></Field>
          <Field label="المهارات"><input className={inputCls} value={tagsToStr(form.skills)} onChange={(e) => set('skills', strToTags(e.target.value))} placeholder="تحليل أداء، تخطيط تدريبي" /></Field>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="المؤهلات التعليمية"><input className={inputCls} value={tagsToStr(form.education)} onChange={(e) => set('education', strToTags(e.target.value))} placeholder="بكالوريوس علوم رياضية" /></Field>
          <Field label="رابط فيديو تعريفي (اختياري)"><input className={inputCls} value={form.intro_video_url || ''} onChange={(e) => set('intro_video_url', e.target.value)} placeholder="https://" /></Field>
        </div>
      </Section>

      <div className="sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/70 bg-white/85 p-3.5 shadow-[0_6px_20px_rgba(15,23,42,0.08)] backdrop-blur-md" data-testid="coach-basics-save-bar">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#16233b] px-6 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:brightness-110 active:scale-95 disabled:opacity-50" data-testid="coach-basics-save-btn">
          <Save size={16} />
          {saving ? 'جارِ الحفظ...' : 'حفظ البيانات'}
        </button>
        <span className="px-1 text-xs text-slate-500">تذكّر حفظ أي تعديل قبل مغادرة الصفحة</span>
      </div>
    </form>
  );
}
